import { ClareStatus, ConnectionTypes, unknownClareStatus } from "@app/shared/model/clare-status.model";
import { ApiResultState, CallState, LoadingState, initialApiObjectState } from "../../root-store/api-result.state";
import { AssignedProjectFeature, ProjectFeatureState } from "../model/project-feature";

import { EntityWithAncestors } from "@app/entity/services/entity-db.service";
import { AlulaDevice } from "@app/project/model/alula-device";
import { AlulaEvent } from "@app/project/model/alula-event";
import { Project } from "@app/project/model/project";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";
import { User } from "@app/user/model/user";
import { AlulaReceiver } from "../model/alula-receiver";
import { AlulaReceiverBind } from "../model/alula-receiver-bind";
import { AlulaReceiverGroup } from "../model/alula-receiver-group";
import { AlulaUser } from "../model/alula-user";
import { CentralStationProject } from "../model/central-station-project";
import { ClareServicePlan } from "../model/clare-service-plan";
import { ClareServiceSubscription } from "../model/clare-service-subscription";
import { PanelSummary } from "../model/panel-summary";
import { ProjectFilter } from "../model/project-filter";
import { ProjectListItemData } from "../model/project-list-item";
import { ProjectView } from "../model/project-view";

export interface ProjectStatusItem {
    projectId: number;
    uuid: string;
    status?: ClareStatus;
    connectionType?: ConnectionTypes;
}

export interface GenericStatusCount<T> {
    key: T; // this one might be updated when we extend the filter
    display: string;
    value: any;
}

export type ServicePlanCount = GenericStatusCount<"Basic" | "Interactive" | "Interactive Plus" | "Interactive Plus LTE">;
export type StatusCount = GenericStatusCount<"online" | "offline" | "unconfigured" | "active" | "suspended">;
export type OccupancyCount = GenericStatusCount<"isOccupied" | "unOccupied">;
export type LockoutTimeCount = GenericStatusCount<"lockoutTime">;

export interface AlulaPanelState {
    device: ApiResultState<AlulaDevice>;
    activeReceiverGroupDevices: ApiResultState<AlulaDevice[]>;
    activeAlarmReportingDevices: ApiResultState<AlulaDevice[]>;
    groups: ApiResultState<AlulaReceiverGroup[]>;
    receivers: ApiResultState<{ string: AlulaReceiver }>;
    binds: ApiResultState<{ string: AlulaReceiverBind[] }>;
    events: { [id: string]: AlulaEvent };
    user: ApiResultState<AlulaUser>;
    monitoringServices: { callState: CallState<any> };
}

export interface ProjectState {
    projects: ApiResultState<ProjectListItemData[]>;
    activeProject: ApiResultState<Project>;
    activeProjectStatus: ApiResultState<ClareStatus>;
    centralStationProjects: ApiResultState<CentralStationProject[]>;
    activeProjectEntity: ApiResultState<EntityWithAncestors>;
    users: ApiResultState<{ [key: string]: User[] }>;
    // Statuses by ProjectId
    projectStatuses?: ApiResultState<ClareDbCollection<ProjectStatusItem>>;
    projectView?: ApiResultState<ProjectView>;
    projectViews: ApiResultState<ClareDbCollection<ProjectView>>;
    selectedSubdealer?: ApiResultState<number>;
    isOccupied?: ApiResultState<boolean>;
    activeProjectFeatures: ApiResultState<AssignedProjectFeature[]>;
    allAdditionalFeatures: ApiResultState<ProjectFeatureState>;
    activePanelSummary: ApiResultState<PanelSummary>;
    connectionType?: ApiResultState<ConnectionTypes>;
}

export interface ServicePlanState extends ApiResultState<ClareDbCollection<ClareServicePlan>> {}
export interface SubscriptionState {
    subscriptions: ApiResultState<ClareDbCollection<ClareServiceSubscription>>;
    activeSubscription: ApiResultState<ClareServiceSubscription | null>;
}

export interface ProjectFiltersState {
    result: ProjectFilter[];
    callState: CallState<ProjectFilter[]>;
}

export const initialSubscriptionState: SubscriptionState = {
    subscriptions: initialApiObjectState(),
    activeSubscription: { result: null, callState: LoadingState.init },
};
export const initialServicePlanState: ServicePlanState = initialApiObjectState({});
export const initialProjectFiltersState: ProjectFiltersState = initialApiObjectState([]);

export const initialProjectState: ProjectState = {
    projects: initialApiObjectState<ProjectListItemData[]>(),
    activeProject: initialApiObjectState<Project>(),
    activeProjectStatus: initialApiObjectState<ClareStatus>(unknownClareStatus),
    centralStationProjects: initialApiObjectState<CentralStationProject[]>(),
    activeProjectEntity: initialApiObjectState<EntityWithAncestors>(),
    users: initialApiObjectState<Record<string, User[]>>(),
    projectStatuses: initialApiObjectState<ClareDbCollection<ProjectStatusItem>>(),
    projectView: initialApiObjectState<ProjectView>(),
    projectViews: initialApiObjectState<ClareDbCollection<ProjectView>>(),
    selectedSubdealer: initialApiObjectState<number>(),
    isOccupied: initialApiObjectState<boolean>(false),
    activeProjectFeatures: initialApiObjectState<AssignedProjectFeature[]>(null),
    allAdditionalFeatures: initialApiObjectState<ProjectFeatureState>(null),
    activePanelSummary: initialApiObjectState<PanelSummary>(null),
    connectionType: initialApiObjectState<ConnectionTypes>(null),
};

export const initialAlulaPanelState: AlulaPanelState = {
    device: initialApiObjectState(),
    activeReceiverGroupDevices: initialApiObjectState(),
    activeAlarmReportingDevices: initialApiObjectState(),
    receivers: initialApiObjectState(),
    groups: initialApiObjectState(),
    binds: initialApiObjectState(),
    events: {},
    user: initialApiObjectState(),
    monitoringServices: { callState: LoadingState.init },
};

export { EmergencyContactState, MunicipalityState } from "./reducers";
